import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getLegendData } from '../utils/lineColorUtils';

/**
 * Floating legend component for water/sewer line color coding
 * Shows line type colors and diameter-based arrow colors
 */
const LineLegend = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const legendData = getLegendData();

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',  // Fixed to viewport, not map container
        bottom: 20,
        left: 10,
        zIndex: 1000,
        minWidth: isExpanded ? 180 : 140,
        maxWidth: 220,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 1,
        overflow: 'visible',
      }}
    >
      {/* Header - Always visible */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          minHeight: 36,
          backgroundColor: '#1976d2',
          color: 'white',
          cursor: 'pointer',
          borderRadius: isExpanded ? '4px 4px 0 0' : 1,
          boxShadow: !isExpanded ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
          '&:hover': {
            backgroundColor: '#1565c0',
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>
          {isExpanded ? 'Line Legend' : 'Legend'}
        </Typography>
        <IconButton
          size="small"
          sx={{ color: 'white', p: 0, ml: 1 }}
        >
          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Conditionally rendered content */}
      {isExpanded && (
        <Box sx={{ p: 1.5 }}>
          {/* Line Type Section */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: '#666',
              textTransform: 'uppercase',
              fontSize: '10px',
              letterSpacing: '0.5px',
            }}
          >
            Line Type
          </Typography>
          <Box sx={{ mt: 0.5, mb: 1.5 }}>
            {legendData.lineTypes.map((item) => (
              <Box
                key={item.type}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.25,
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 3,
                    backgroundColor: item.color,
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: '11px' }}>
                  {item.type}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Diameter Section */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: '#666',
              textTransform: 'uppercase',
              fontSize: '10px',
              letterSpacing: '0.5px',
            }}
          >
            Pipe Diameter (Arrow Color)
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {legendData.diameterBuckets.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.25,
                }}
              >
                <Box
                  sx={{
                    width: 0,
                    height: 0,
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderBottom: `10px solid ${item.color}`,
                    transform: 'rotate(90deg)',
                    filter: item.color === '#FFFFFF' ? 'drop-shadow(0 0 1px #888)' : 'none',
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: '11px', ml: 0.5 }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default LineLegend;
